import { useEffect } from 'react'
import { formValueSelector, reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import { CSSTransition } from 'react-transition-group'
import { Error, Loader } from '@myap/ui-library/cjs/components'
import { DATETIME_FORMATS, formatDateTime } from '@myap/ui-library/esm/date'
import { AP_AGREEMENT_FORM, AP_AGREEMENT_FIELDS as FIELDS } from '../../../constants/SetupConstants'
import { StepTracker, StepButtons } from '..'
import { finishCoordinatorSetup, getUserDetails } from '../../../actions/user'
import { changeStep, updateOrgSettings, fetchOrgSettings } from '../../../actions/settingsOrg'
import { isFeatureEnabled } from '../../../selectors/features'
import ApAgreementForm from './ApAgreement'
import { getDpaAgreement } from '../../../actions/dpaAgreement'

const mapStateToProps = state => {
  const {
    user: {
      data: { selectedOrgId },
    },
    settingsOrg,
    settingsEducationPeriod: { selectedEducationPeriod, availableEducationPeriods },
    dpaAgreement: {
      data: dpaAgreementData,
      fetching: dpaAgreementFetching,
      fetchError: dpaAgreementError,
    },
  } = state
  const orgSettings = settingsOrg[selectedOrgId].data
  const { setupStep } = settingsOrg
  const bigFutureEnabled = isFeatureEnabled(state, 'BIGFUTURE')
  const selector = formValueSelector(AP_AGREEMENT_FORM)
  return {
    orgSettings,
    setupStep,
    selectedOrgId,
    selectedEducationPeriod,
    educationPeriod: availableEducationPeriods[selectedEducationPeriod]?.descr,
    bigFutureEnabled,
    dpaAgreementData,
    dpaAgreementFetching,
    dpaAgreementError,
    initialValues: {
      [FIELDS.AP_AGREEMENT_IND.name]: orgSettings?.participationForm?.coordinatorSigned || false,
    },
    currentValues: {
      [FIELDS.AP_AGREEMENT_IND.name]: selector(state, FIELDS.AP_AGREEMENT_IND.name),
    },
  }
}

const ApAgreement = ({
  backStep,
  error,
  initialValues,
  currentValues,
  selectedOrgId,
  getDpaAgreement,
  dpaAgreementData,
  dpaAgreementFetching,
  dpaAgreementError,
}) => {
  useEffect(() => {
    if (selectedOrgId) {
      getDpaAgreement(selectedOrgId)
    }
  }, [selectedOrgId, getDpaAgreement])

  if (dpaAgreementFetching) {
    return <Loader />
  }

  if (dpaAgreementError) {
    return <Error message={dpaAgreementError?.message || 'Unable to load DPA agreement.'} />
  }

  return (
    <div>
      <StepTracker />
      {dpaAgreementData ? (
        <div>
          <p>
            Your school has signed a Digital Privacy Agreement with the College Board. This DPA was
            signed by the following:
          </p>
          <p>
            {dpaAgreementData.signee} on{' '}
            {formatDateTime(dpaAgreementData.signingDate, DATETIME_FORMATS.shortMonthDayYear)}.
          </p>
        </div>
      ) : (
        <>
          <CSSTransition
            classNames="animate-fade"
            in={true}
            timeout={500}
            appear={true}
            enter={false}
            exit={false}
          >
            <ApAgreementForm
              error={error}
              initialValues={initialValues}
              currentValues={currentValues}
              className="container"
            />
          </CSSTransition>
        </>
      )}
      <div style={{ marginTop: 50 }}>
        <StepButtons
          backStep={backStep}
          form={AP_AGREEMENT_FORM}
          buttonText={dpaAgreementData ? 'Continue' : 'Save and Continue'}
        />
      </div>
    </div>
  )
}

export default connect(mapStateToProps, {
  updateOrgSettings,
  fetchOrgSettings,
  finishCoordinatorSetup,
  getUserDetails,
  getDpaAgreement,
  changeStep,
})(
  reduxForm({
    form: AP_AGREEMENT_FORM,
    onSubmit: (data, dispatch, { updateOrgSettings, orgSettings, dpaAgreementData }) => {
      if (dpaAgreementData) {
        updateOrgSettings({
          ...orgSettings,
          participationForm: {
            ...orgSettings.participationForm,
            dpaAgreement: {
              signee: dpaAgreementData.signee,
              signingDate: dpaAgreementData.signingDate,
              expirationDate: dpaAgreementData.expirationDate,
              updatedDate: dpaAgreementData.updatedDate,
            },
            coordinatorSigned: true,
            complete: true,
          },
        })
      } else {
        updateOrgSettings({
          ...orgSettings,
          participationForm: {
            ...orgSettings.participationForm,
            coordinatorSigned: true,
            complete: true,
          },
        })
      }
    },
    onSubmitSuccess: (
      data,
      dispatch,
      {
        setupStep,
        bigFutureEnabled,
        selectedOrgId,
        orgSettings,
        selectedEducationPeriod,
        finishCoordinatorSetup,
        fetchOrgSettings,
        getUserDetails,
        changeStep,
      }
    ) => {
      const bigFutureApplicable = orgSettings?.bigFuture !== undefined
      window.scrollTo(0, 0)
      if (!bigFutureEnabled || !bigFutureApplicable) {
        // last step
        changeStep(null)
        finishCoordinatorSetup(selectedOrgId, orgSettings.examAdministration.orderByAnotherSchool)

        // Fetch the org settings after saving because backend copies coordinator's
        // address into billing address and we want this to display on Settings Page.
        fetchOrgSettings(selectedOrgId, selectedEducationPeriod)

        // this clears the alarms icons throughout app as they are
        // populated with user.role data. This organization-info
        // saving does not automatically refresh the user details.
        getUserDetails()
      } else {
        // one more step to go for big future...
        changeStep(setupStep + 1)
      }
    },
  })(ApAgreement)
)
